import React, { useEffect, useRef, useState } from "react";
import { RCCButton, RCCDropDownItem, RCCGrid, RCCHidden, RCCHookToast, RCCIcon, RCCInput, RCCPopover, RCCServicesApi, RCCTooltip } from "react-common-components";
import { useForm } from "react-hook-form";
import OptionsColumn from "src/components/DefaultColumnsList/Options";
import Select from "src/components/Select";
import Title from "src/components/Title";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import { v4 } from "uuid";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import {ISwotItens, SWOTType,ISwotItem} from "../../interface";
import { IRefAlterDimensionModal, IRefModalSWOTBounds, IRefModalSWOTMapBounds, IRefSwotDetailModal } from "./components/interface";
import ModalSWOTMapBounds from "./components/MapBounds";
import SwotDetailModal from "./components/ItemDetail";
import AlterDimensionModal from "./components/ModalAlterDimension";
import AddOrChangeSchema from "./schemas";
import { Ulcontainer } from "./styles";
import swotLabels, { useIntensity, useTendencySO } from "../../SwotLabels";
import ModalSWOTBounds from "./components/ModalSWOTBounds";

const SWOTItens: React.FC<ISwotItens> = ({
	data,
	type,
	dimensionId,
	SWOTId,
	swotTitle,
	canChange
}) => {
	const { translate } = useTranslation();
	const { addToast} = RCCHookToast();
	const {confirm} = useConfirmModal();
	const refModalSWOTMapBounds = useRef<IRefModalSWOTMapBounds>(null);
	const refModalDetailItem = useRef<IRefSwotDetailModal>(null);
	const refAlterDimension = useRef<IRefAlterDimensionModal>(null);
	const refSWOTbonds = useRef<IRefModalSWOTBounds>(null);
	
	const [swotItens,setSwotItens] = useState(data);

	const {
		register,
		unregister,
		getValues,
		control,
		reset,
		setError,
		clearErrors
	}= useForm();
	
	const setText = (swotType:SWOTType) => {
		switch (swotType) {
		case SWOTType.STRENGTHS:
			return <Title color="defaultColor">{translate("Aspectos internos que contribuem para a estratégia da sua empresa")}.</Title>;
		case SWOTType.WEAKNESSES:
			return <Title color="defaultColor">{translate("Aspectos internos que dificultam a estratégia da sua empresa")}.</Title>;
		case SWOTType.OPPORTUNITIES:
			return <Title color="defaultColor">{translate("Aspectos externos que contribuem para a estratégia da sua empresa")}.</Title>;
		default:
			return <Title color="defaultColor">{translate("Aspectos externos que dificultam a estratégia da sua empresa")}.</Title>;
		}
	};

	const {importance,intensity,urgency,tendencySO,tendencyWT} = swotLabels(translate);	

	const colOptions = (row: ISwotItem) => (
		<OptionsColumn>

			<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
				<RCCDropDownItem 
					selfEnclose
					onClick={() => {refModalSWOTMapBounds.current?.openSWOTItensHierarchyObjectives(row.mes002Id); }}
				>
					<RCCIcon 
						name="link" 
					/>
					{translate("Associar mapa")}
				</RCCDropDownItem>
				<RCCDropDownItem 
					selfEnclose
					onClick={() => { 
						refSWOTbonds.current?.openModalActionsHierarchySWOT(row.mes002Mes001Id,row.mes002Id);
					}}
				>
					<RCCIcon 
						name="shuffle" 
					/>
					{translate("Vincular itens da SWOT")}
				</RCCDropDownItem>
				<RCCDropDownItem 
					selfEnclose
					onClick={() => { 
						refAlterDimension.current?.actionOpenModal({id:row.mes002Id,title:row.mes002Titulo,dimensionText:row.dimensionText},
							(newDimension) => {
								if(newDimension !== row.mes002Taa011Id)
									deleteItem(row);
							});
					}}
				>
					<RCCIcon 
						name="exchange" 
					/>
					{translate("Alterar dimensão")}
				</RCCDropDownItem>														
			</CheckPermission>
		
			<RCCDropDownItem 
				selfEnclose
				onClick={() => {refModalDetailItem.current?.actionOpenModal(row.mes002Id,2); }}
			>
				<RCCIcon 
					name="mapSigns" 
				/>
				{translate("Rastreabilidade")}
			</RCCDropDownItem>
			
			<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
				<RCCDropDownItem
					selfEnclose
					onClick={() => { 
						setSwotItens((prevState) => 
							prevState.map((x) => x.mes002Id === row.mes002Id ? {...x,onChange:true} : x)
						);
					}}
				>
					<RCCIcon 
						name="pencil" 
					/>
				    {translate("Alterar")}
				</RCCDropDownItem>														
			</CheckPermission>
			
			<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
				<RCCDropDownItem 
					selfEnclose
					onClick={() => {
						deleteItem(row,true);
					}}
				>
					<RCCIcon 
						name="trash" 
					/>
				    {translate("Excluir")}
				</RCCDropDownItem>															
			</CheckPermission>
		</OptionsColumn>
	);

	const addField = () => {
		unregister();
		const newItem = {
			id:v4(),
			mes002Id:0,
			onChange:true,
			mes002Mes001Id:SWOTId||0,
			mes002Importancia:1,
			mes002IntensidadeUrg:1,
			mes002Tendencia:1
		} as ISwotItem;
		setSwotItens((prevState) => [newItem,...prevState]);
	};

	const saveItem = async(index:number, isChange?: boolean) => {
		const row = getValues(`[${index}]`);

		try {
			clearErrors();
			
			await AddOrChangeSchema(translate).validate({...row},{
				abortEarly: false
			});
			
			let response;
			row.mes002Mes001Id = SWOTId||0;
			row.mes001Titulo = swotTitle ?? "";
			if(!isChange){
				row.mes002Taa011Id = dimensionId;
				row.mes002Secao = type;
				response = await RCCServicesApi.postRequest({
					method: "SWOT/InsertSwotItem",
					body: {
						...row	
					}
				});

			}else{
				response = await RCCServicesApi.putRequest({
					method: "SWOT/UpdateSwotItem",
					body: {
						...row
					}
				});
			}
			
			addToast({
				color: "green",
				description: !isChange ? translate("Item adicionado com sucesso") : translate("Item alterado com sucesso"),
				title: translate("Sucesso")
			});
			
			if(!isChange)
				row.mes002Id = response.data;

			setSwotItens((prevState) => 
				prevState.map((x) => x.id === row.id ? 
					{
						...x,
						mes002Id:row.mes002Id,
						mes002Titulo:row.mes002Titulo,
						mes002Identificador:row.mes002Identificador,
						mes002Importancia:row.mes002Importancia,
						mes002IntensidadeUrg:row.mes002IntensidadeUrg,
						mes002Tendencia:row.mes002Tendencia,
						mes002Pontuacao:row.mes002Importancia * row.mes002IntensidadeUrg * row.mes002Tendencia,
						onChange:false
					}
					: 
					x
				)
			);

		} catch (error) {
			if(getValidationErrors(error, setError))
				return;

			standardErrorMessages(error, !isChange ? translate("Erro ao adicionar item") : translate("Erro ao alterar item"), addToast);
		}
	};

	const deleteItem = async(row:ISwotItem,useRequest:boolean=false) => {
		if(useRequest){
			confirm(
				translate("Exclusão do item de SWOT"),
				`${translate("Deseja realmente excluir o item")}: ${row.mes002Titulo} ?`,
				async () => {
					await RCCServicesApi.deleteRequest({
						method: "SWOT/DeleteSwotItem",
						urlParameters: {
							id: row.mes002Id,
							title: row.mes002Titulo,
							swotId: row.mes002Mes001Id,
							swotTitle
						}
					});

					addToast({
						color: "green",
						description: translate("Item excluído com sucesso"),
						title: translate("Sucesso")
					});

					unregister();
					setSwotItens((prevState) => 
						prevState.filter((x) => x.id !== row.id)
					);
				},
				(err) => {
					standardErrorMessages(err, translate("Erro ao excluir item"), addToast);
				},
				true
			);
		}else{
			unregister();
			setSwotItens((prevState) => 
				prevState.filter((x) => x.id !== row.id)
			);
		}
	};

	useEffect(() => {
		setSwotItens(data);
		reset();
	}, [data]);

	return (
		<RCCGrid container>
			<RCCGrid container item justifyContent="space-between">
				{setText(type)}
				<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
					{canChange &&
				<RCCTooltip
					text={translate("Adicionar item")}
					color="purple">
					<RCCButton 
						onClick={() => {addField();}} 
						size="sm" 
						color="purple" 
						icon={{name:"plus"}} 
						layout="isRounded"/>
				</RCCTooltip>
					}													
				</CheckPermission>
			</RCCGrid>
			<RCCGrid container item>
				<Ulcontainer>
					<li className="list-component-header" key={v4() }>
						<RCCGrid item container
							xs={8}
							sm={3}
							alignItems="baseline"
						>
							<Title color="defaultColor" size="sm">
								{translate("Item")}
							</Title>
						</RCCGrid>
						<RCCGrid item container
							xs={8}
							sm={1}
							alignItems="baseline"
						>
							<Title color="defaultColor" size="sm">
								{translate("Código")}
							</Title>
						</RCCGrid>
						<RCCGrid item container
							xs={8}
							sm={2}
							alignItems="baseline"
						>
							<Title color="defaultColor" size="sm">
								{translate("Importância")}
							</Title>
							<RCCPopover
								header={{title:translate("Importância")}}
								body={{custom:
									<span className="popover-body">
										{translate("Aqui é preciso entender o quanto o item tem de impacto na estratégia da empresa")}, {translate("apartir de")} <b>{translate("Sem importância")}</b>, {translate("o valor mais baixo, até")} <b>{translate("Totalmente importante")}</b>, {translate("o valor mais alto")}.
									</span>
								}}
							>
								<RCCIcon
									name="infoCircle"
								/>
							</RCCPopover>
						</RCCGrid>
						<RCCGrid item container
							xs={8}
							sm={2}
							alignItems="baseline"
						>
							<Title color="defaultColor" size="sm">
								{useIntensity(type) ? translate("Intensidade") : translate("Urgência") }
							</Title>
							<RCCPopover
								header={{title:useIntensity(type) ? translate("Intensidade") : translate("Urgência")}}
								body={{text:`${translate("O quanto o item pode escalar ou diminuir de impacto na sua estratégia")}, ${translate("com o valor mais baixo representando que ele não tem como impactar mais e o valor mais alto representando que ele ainda pode impactar muito")}.`}}
							>
								<RCCIcon
									name="infoCircle"
								/>
							</RCCPopover>
						</RCCGrid>
						<RCCGrid item container
							xs={8}
							sm={2}
							alignItems="baseline"
						>
							<Title color="defaultColor" size="sm">
								{translate("Tendência")}
							</Title>
							<RCCPopover
								header={{title:translate("Tendência")}}
								body={{text:`${translate("Aqui será avaliado o quanto o item tem a tendência de se manter ou deixar de existir")}. ${translate("A tendência de se manter é o valor mais alto e a tendência de deixar de existir é o valor mais baixo")}.`}}
							>
								<RCCIcon
									name="infoCircle"
								/>
							</RCCPopover>
						</RCCGrid>
						<RCCGrid item container
							xs={8}
							sm={1}
							alignItems="baseline"
						>
							<Title color="defaultColor" size="sm">
								{translate("Pontos")}
							</Title>
							<RCCPopover
								header={{title:translate("Pontos")}}
								body={{text:`${translate("A pontuação é dada pela multiplicação dos pontos da importância")}, ${useIntensity(type) ? translate("Intensidade") : translate("Urgência")} ${translate("e")} ${translate("tendência")}.`}}
							>
								<RCCIcon
									name="infoCircle"
								/>
							</RCCPopover>
						</RCCGrid>
					</li>
					{swotItens.map((item,index) => 
						(
							<li className="list-component-child" key={v4() }>
								{item.onChange ? 
									<RCCGrid container spacing={1}>
										<RCCGrid item container
											xs={12}
											sm={4}
											md={3}
										>
											<input 
												{...register(`[${index}].id`)} 
												name={`[${index}].id`}
												defaultValue={item.id}
												type="hidden"
											/>
											<input 
												{...register(`[${index}].mes002Id`)} 
												name={`[${index}].mes002Id`}
												defaultValue={item.mes002Id}
												type="hidden"
											/>
											
											<RCCInput
												register={register}
												name={`[${index}].mes002Titulo`}
												defaultValue={item.mes002Titulo}
												label={translate("Título")}
												required
												maxLength={200}
											/>
										</RCCGrid>
										<RCCGrid item container
											xs={12}
											sm={2}
											md={1}
										>
											<RCCInput
												register={register}
												name={`[${index}].mes002Identificador`}
												defaultValue={item.mes002Identificador}
												label={translate("Código")}
												maxLength={15}
											/>
										</RCCGrid>
										<RCCGrid item container
											xs={12}
											sm={4}
											md={2}
										>
											<Select 
												control={control}
												name={`[${index}].mes002Importancia`}
												options={importance}
												label={translate("Importância")}
												defaultValue={item.mes002Importancia.toString()}
											/>
										</RCCGrid>
										<RCCGrid item container
											xs={12}
											sm={4}
											md={2}
										>
											<Select 
												control={control}
												name={`[${index}].mes002IntensidadeUrg`}
												options={useIntensity(type) ? intensity : urgency} 
												label={useIntensity(type) ? translate("Intensidade") : translate("Urgência") }
												defaultValue={item.mes002IntensidadeUrg.toString()}
											/>
										</RCCGrid>
										<RCCGrid item container
											xs={8}
											sm={4}
											md={2}
										>
											<Select 
												control={control}
												name={`[${index}].mes002Tendencia`}
												options={useTendencySO(type) ? tendencySO : tendencyWT}
												label={translate("Tendência")}
												defaultValue={item.mes002Tendencia.toString()}
											/>
										</RCCGrid>
										<RCCGrid item container
											alignContent="center"
											xs={4}
											sm={2}
										>
											<RCCTooltip
												text={translate("Cancelar")}
												color="red">
												<RCCButton 
													onClick={() => (
														item.mes002Id === 0 ? 
															deleteItem(item) 
															: 
															setSwotItens((prevState) => prevState.map((x) => x.id === item.id ? {...x,onChange:false} : x))
													)} 
													size="sm" 
													icon={{name:"close",strokeWidth:2}}
													color="red" 
													layout="isRounded"
												/>
											</RCCTooltip>
											
											<RCCTooltip
												text={translate("Ok")}
												color="green">
												<RCCButton 
													onClick={() => {
														saveItem(index, String(item.mes002Id) !== "0");
													}} 
													size="sm" 
													color="green" 
													icon={{name:"check"}} 
													layout="isRounded"
												/>
											</RCCTooltip>
											
										</RCCGrid>
									</RCCGrid>
									:
									<>
										<RCCGrid item container
											xs={12}
											sm={3}
										>
											<span>
												<RCCHidden smUp><b>{translate("Item")}:</b> </RCCHidden>
												{item.dimensionText && 
													<span style={{color:"var(--grey)"}}>[{item.dimensionText}] </span>
												}
												{item.mes002Titulo}
											</span>
										</RCCGrid>
										<RCCGrid item container
											xs={12}
											sm={1}
										>
											<span>
												<RCCHidden smUp><b>{translate("Código")}:</b> </RCCHidden>{item.mes002Identificador}
											</span>
										</RCCGrid>
										<RCCGrid item container
											xs={12}
											sm={2}
										>
											<span>
												<RCCHidden smUp><b>{translate("Importância")}:</b> </RCCHidden>{importance[item.mes002Importancia-1].label}
											</span>
										</RCCGrid>
										<RCCGrid item container
											xs={12}
											sm={2}
										>
											
											{useIntensity(type) ? 
												<span><RCCHidden smUp><b>{translate("Intensidade")}:</b> </RCCHidden>{intensity[item.mes002IntensidadeUrg-1].label}</span>
												:
												<span><RCCHidden smUp><b>{translate("Urgência")}:</b> </RCCHidden>{urgency[item.mes002IntensidadeUrg-1].label}</span>
											}
											
										</RCCGrid>
										<RCCGrid item container
											xs={12}
											sm={2}
										>
											<span>
												<RCCHidden smUp><b>{translate("Tendência")}:</b> </RCCHidden>{useTendencySO(type) ? tendencySO[item.mes002Tendencia-1].label: tendencyWT[item.mes002Tendencia-1].label}
											</span>
										</RCCGrid>
										<RCCGrid item container
											xs={12}
											sm={2}
											alignItems="center"
										>
											<RCCGrid item container
												xs={6}
											>
												<span>
													<RCCHidden smUp><b>{translate("Pontuação")}:</b> </RCCHidden>{item.mes002Pontuacao}
												</span>
											</RCCGrid>
											{canChange &&
												<RCCGrid item container
													justifyContent="flex-end"
													xs={6}
												>
													{colOptions(item)}
												</RCCGrid>
											}
										</RCCGrid>
									</>
								}
							</li>
						)
					)}
				</Ulcontainer>
			</RCCGrid>
			<ModalSWOTMapBounds ref={refModalSWOTMapBounds}/>
			<ModalSWOTBounds ref={refSWOTbonds}/>
			<SwotDetailModal ref={refModalDetailItem}/>
			<AlterDimensionModal ref={refAlterDimension}/>
		</RCCGrid>
	);
};

export default  React.memo(SWOTItens);
